import { Map as immutableMap } from 'immutable';
import { getField, updateField } from 'vuex-map-fields';
import randomId from '@/utils/randomId';
import objectValue from '@/utils/objectValue';
import {
  ELEMENTS_TYPES,
  ELEMENTS_TYPES_ALLOWED_CHILDREN,
  ELEMENTS_TYPES_ALLOWED_TEXT,
  ELEMENTS_TYPES_BRANCH,
  ELEMENTS_TYPES_BRANCH_ACTIONS,
  ELEMENTS_TYPES_BRANCH_ACTIONS_KEYS,
  ELEMENTS_TYPES_BRANCH_AFTER_ACTIONS,
  ELEMENTS_TYPES_COUNTABLE,
  ELEMENTS_TYPES_GROUPS,
  ELEMENTS_TYPES_LABELS,
  ELEMENTS_TYPES_SECOND_MENU,
} from '@/api/elements';
import swapArrayItems from '@/utils/swapArrayItems';

const checks = {
  getAllowedChildren: function (type) {
    if (!type) {
      return false;
    }
    // Find all allowed types.
    let allowedChildren = ELEMENTS_TYPES_ALLOWED_CHILDREN[type];
    if (!Array.isArray(allowedChildren)) {
      return false;
    }
    for (const _type of allowedChildren) {
      const array = ELEMENTS_TYPES_ALLOWED_CHILDREN[_type];
      if (ELEMENTS_TYPES_SECOND_MENU.includes(_type)
        && Array.isArray(array)
      ) {
        allowedChildren = allowedChildren.concat(array);
      }
    }
    return allowedChildren;
  },
  isAllowedToDrop: function (type, anotherType) {
    const array = this.getAllowedChildren(type);
    return Array.isArray(array) && array.includes(anotherType);
  },
  checkAllowDrop: function ({
    item,
    anotherItem,
    nestedChildrenIds = [],
  }) {
    return !!item && !!anotherItem
      // Exclude self
      && anotherItem.id !== item.id
      // Exclude parent
      && anotherItem.from !== item.id
      // Exclude nested children
      && !(Array.isArray(nestedChildrenIds) && nestedChildrenIds.includes(item.id))
      // Filter by type
      && this.isAllowedToDrop(item.type, anotherItem.type);
  },
};

const getChildrenIds = ({context, id, childrenOnly, excludeId, excludeItemsInGroup}) => {
  const items = [...context.state.items];

  const notInGroup = type => {
    console.log(Object.values(ELEMENTS_TYPES_GROUPS));
    return !Object.values(ELEMENTS_TYPES_GROUPS).includes(type);
  }

  const initItemsIDs = items
    .filter(i => i.id === id || i.relatedToItemId === id)
    .map(i => i.id);

  let ids = [];

  // Find all nested children.
  const findNestedChildrenIds = parentId => {
    if (parentId === excludeId) {
      return false;
    }

    ids.push(parentId);

    const children = context.getters.getItemsChildren(parentId);

    if (Array.isArray(children) && children.length > 0) {
      for (const item of children) {
        if (item.id !== excludeId
          && (
            !excludeItemsInGroup || notInGroup(item.type)
          )
        ) {
          ids.push(item.id);

          findNestedChildrenIds(item.id);
        }
      }
    }
  };
  for (const initID of initItemsIDs) {
    findNestedChildrenIds(initID);
  }

  // Remove duplicates.
  ids = [...new Set(ids)];

  if (childrenOnly) {
    const index = ids.indexOf(id);
    if (index > -1) {
      ids.splice(index, 1);
    }
  }

  return ids;
}

const STATE = immutableMap({
  date: Date.now(),
  loading: true,
  draggingItem: null,
  focusId: null,
  focusIdInput: null,
  draggedOverId: null,
  items: [],
  hoveredItem: false,
  editingNodeText: null,
  layoutScale: 1,
});

export const state = () => STATE.toJS();

export const getters = {
  getField,
  isDraggingNode: state => state.draggingItem && Object.keys(state.draggingItem).length > 0,
  rootNode: state => Array.isArray(state.items) && state.items.find(i => i.isRoot),
  getItems: (state, getters) => ({showDeleted = false}) => {
    // Prepare counting.
    const count = {};
    for (const type of ELEMENTS_TYPES_COUNTABLE) {
      count[type.key] = 0;
    }

    const draggingItemId = objectValue(state, 'draggingItem.id');
    const draggingItemFrom = objectValue(state, 'draggingItem.from');

    const items = !showDeleted
      ? [...state.items]
      : [...state.items.filter(i => i.deleted)];

    let r = [];
    for (const i of items) {
      // Check siblings.
      i._isSibling = draggingItemFrom === i.from;

      // Count items.
      const countable = ELEMENTS_TYPES_COUNTABLE.find(t => t.types.includes(i.type));
      if (countable) {
        ++count[countable.key];
        i._typeIndex = count[countable.key];
      }

      // Add label.
      i._label = ELEMENTS_TYPES_LABELS[i.type];

      // Label for countable.
      if (i._typeIndex
        && countable.label
      ) {
        i._label = `${countable.label} ${i._typeIndex}`;
      }

      // Edit text
      i._editableText = ELEMENTS_TYPES_ALLOWED_TEXT.includes(i.type);

      // Count children.
      // TODO: Check if this is still useful.
      i._childrenCount = state.items
        .filter(child => child.from === i.id)
        .length;

      i._allowDropInTree = checks.checkAllowDrop({
        item: i,
        anotherItem: state.draggingItem,
        nestedChildrenIds: getters.nestedChildrenIds(draggingItemId),
      });

      i._allowDropInGroup =
        // Exclude self
        draggingItemId !== i.id
        // Allow sibling
        && i._isSibling;

      r.push(i);
    }
    return r;
  },
  getItem: (state, getters) =>
    id =>
      Array.isArray(getters.getItems({}))
      && getters.getItems({}).find(i => i.id === id),
  getItemsChildren: (state, getters) => id => Array.isArray(getters.getItems({})) && getters.getItems({}).filter(i => i.from === id),
  getItemsGrandsons: (state, getters) => id => Array.isArray(getters.getItemsChildren(id)) && getters.getItemsChildren(id)
    .map(i => getters.getItemsChildren(i.id))
    .flat()
    .filter(Boolean),
  getTrash: (state, getters) => {
    const items = getters.getItems({showDeleted: true});
    return Array.isArray(items) && items;
  },
  getAllowedTypes: state => type => {
    let allowed = ELEMENTS_TYPES_ALLOWED_CHILDREN[type];
    if (!Array.isArray(allowed)) {
      return [];
    }
    return allowed.map(t => {
      const result = { key: t };
      const countable = ELEMENTS_TYPES_COUNTABLE.find(countableType => countableType.types.includes(t));
      if (countable) {
        const amount = state.items.filter(i => i.type === t).length;
        result.disabled = amount >= countable.max;
      }
      return result;
    });
  },
  nestedChildrenIds: state => parentId => {
    let ids = [];
    const findInChildrenTree = parentId => {
      ids.push(parentId);

      const children = Array.isArray(state.items) && state.items.filter(i => i.from === parentId);
      if (children.length > 0) {
        for (const item of children) {
          ids.push(item.id);

          findInChildrenTree(item.id);
        }
      }
    };
    findInChildrenTree(parentId);
    ids = [...new Set(ids)];
    return ids;
  },
  exportItems: (state, getters) => {
    const items = getters.getItems({showDeleted: false});
    if (!Array.isArray(items)) {
      return false;
    }

    const task = {
      greeting: {},
      goodbye: {},
      scenario: {},
    };

    // Find all branches.
    let branches = items.filter(i => ELEMENTS_TYPES_BRANCH.includes(i.type));

    const isGroup = (type) => {
      return Object.keys(ELEMENTS_TYPES_GROUPS).includes(type);
    }

    const prepareBranch = (branch) => {
      let o = {
        message: {
          text: branch.text,
          audio: null,
        },
      };

      const child = objectValue(getters.getItemsChildren(branch.id), '0');

      // Show child main action.
      if (child && child.type === ELEMENTS_TYPES.SMART_PAUSE) {
        o.interactive_pause = 1;
      }
      if (child && child.type === ELEMENTS_TYPES.RECOGNIZE_ANSWER_GROUP_LONG) {
        o.recognize_long_answer = 1;
      }
      if (child && child.type === ELEMENTS_TYPES.RECOGNIZE_ANSWER_GROUP_SHORT) {
        o.recognize_short_answer = 1;
      }

      // Add behavior section.
      const prepareBehaviorElement = ({ behaviorChainIds, element, elementIndex }) => {
        // Remove parent element.
        behaviorChainIds.splice(0, 1);

        const preparedElement = {};

        if (elementIndex > 0) {
          preparedElement.keywords = element.text;
        }

        preparedElement.actions = [];
        preparedElement.after_action = {};

        // Use `.some()` to stop filtering.
        behaviorChainIds.some(id => {
          const item = getters.getItem(id);
          const check = ELEMENTS_TYPES_BRANCH_ACTIONS.includes(item.type);

          if (check) {
            const defaultValue = ELEMENTS_TYPES_ALLOWED_TEXT.includes(item.type)
              ? ''
              : 1;
            preparedElement.actions.push({
              [ELEMENTS_TYPES_BRANCH_ACTIONS_KEYS[item.type]]: item.text || defaultValue,
            })
          }

          return !check;
        });

        // Default after action is ELEMENTS_TYPES.GOODBYE.
        let goName = 'end';
        let nextBranchIndex = 1;

        const afterActionId = behaviorChainIds
          .find(id => {
            const item = getters.getItem(id);
            return ELEMENTS_TYPES_BRANCH_AFTER_ACTIONS.includes(item.type);
          });

        if (afterActionId) {
          const afterAction = getters.getItem(afterActionId);

          if (afterAction && afterAction.type === ELEMENTS_TYPES.MESSAGE) {
            goName = 'goto';
            nextBranchIndex = branches.findIndex(i => i.id === afterAction.id);
          }

          if (afterAction && afterAction.type === ELEMENTS_TYPES.LINK_TO_MESSAGE) {
            const relatedToItem = getters.getItem(afterAction.relatedToItemId);
            const relatedToItemType = objectValue(relatedToItem, 'type');
            if (relatedToItemType === ELEMENTS_TYPES.MESSAGE) {
              goName = !afterAction.returnToParent
                ? 'goto'
                : 'reaskfrom';
              nextBranchIndex = branches.findIndex(i => i.id === afterAction.relatedToItemId);
            }
          }
        }
        preparedElement.after_action[goName] = nextBranchIndex;

        return preparedElement;
      };

      if (child && child.type === ELEMENTS_TYPES.SMART_PAUSE) {
        o.behavior = {
          0: prepareBehaviorElement({
            behaviorChainIds: getters.nestedChildrenIds(child.id),
            elementIndex: 0,
            element: child,
          }),
        };
      }

      if (child && isGroup(child.type)) {
        o.behavior = {};
        const behaviorChildren = getters.getItemsChildren(child.id);
        for (const [elementIndex, element] of behaviorChildren.entries()) {
          o.behavior[elementIndex] = prepareBehaviorElement({
            behaviorChainIds: getters.nestedChildrenIds(element.id),
            elementIndex,
            element,
          });
        }
      }

      return o;
    };

    const greeting = items.find(i => i.type === ELEMENTS_TYPES.GREETING);
    if (greeting) {
      task.greeting = prepareBranch(greeting);
    }

    const goodbye = items.find(i => i.type === ELEMENTS_TYPES.GOODBYE);
    task.goodbye = {
      message: {
        text: objectValue(goodbye, 'text', null),
        audio: null,
      },
    };

    for (const [branchIndex, branch] of branches.entries()) {
      task.scenario[branchIndex] = prepareBranch(branch);
    }

    return task;
  },
};

export const mutations = {
  reset(state) {
    Object.assign(state, {
      ...STATE.toJS(),
      date: Date.now(),
    });
  },
  resetTempVariables(state) {
    const initState = STATE.toJS();
    state.editingNodeText = initState.editingNodeText;
    state.draggingItem = initState.draggingItem;
    state.hoveredItem = initState.hoveredItem;
    state.focusId = initState.focusId;
    state.focusIdInput = initState.focusIdInput;
  },
  updateField,
  addRootNode(state) {
    const newObject = {
      id: randomId(),
      isRoot: true,
      type: ELEMENTS_TYPES.GREETING,
      createdAt: Date.now(),
    };
    state.items.push(newObject);
  },
};

export const actions = {
  async reset(context) {
    context.commit('reset');
    await context.dispatch('checkRootNode');
  },

  // async load(context) {
  //   context.commit('updateField', {path: 'loading', value: true});
  //   await context.commit('updateField', {path: 'items', value: items});
  //   context.commit('updateField', {path: 'loading', value: false});
  // },

  checkRootNode(context) {
    if (context.getters.rootNode) {
      return false;
    }

    context.commit('reset');
    context.commit('addRootNode');
  },

  /**
   * Add node
   * @param context
   * @param id
   * @param from
   * @param isNew
   * @param type
   * @param text
   * @param returnToParent
   * @param relatedToItemId
   * @param isSecondAction
   * @param addDefaultChild
   * @returns {Promise<*>}
   */
  async addNode(context, {
    id,
    from,
    isNew,
    type,
    text,
    returnToParent,
    relatedToItemId,
    isSecondAction = false,
    addDefaultChild = true,
  }) {
    id = typeof id !== 'undefined'
      ? id
      : objectValue(context, 'state.draggingItem.id', randomId());
    from = typeof from !== 'undefined'
      ? from
      : objectValue(context, 'state.draggingItem.from');
    isNew = typeof isNew !== 'undefined'
      ? isNew
      : objectValue(context, 'state.draggingItem.isNew');
    type = typeof type !== 'undefined'
      ? type
      : objectValue(context, 'state.draggingItem.type');
    text = typeof text !== 'undefined'
      ? text
      : objectValue(context, 'state.draggingItem.text', null);
    relatedToItemId = typeof relatedToItemId !== 'undefined'
      ? relatedToItemId
      : objectValue(context, 'state.draggingItem.relatedToItemId');
    returnToParent = typeof returnToParent !== 'undefined'
      ? returnToParent
      : objectValue(context, 'state.draggingItem.returnToParent');

    if (type === ELEMENTS_TYPES.GOODBYE) {
      const items = context.getters.getItems({});
      const original = items.find(i => i.type === type);

      if (original) {
        relatedToItemId = original.id;
        type = ELEMENTS_TYPES.LINK_TO_MESSAGE;
      }
    }

    const newObject = {
      id,
      from,
      type,
      text,
      returnToParent,
      createdAt: Date.now(),
    };

    const previousItem = Array.isArray(context.state.items) && context.state.items.find(i => i.from === from);

    // Move previous one after dropped element or delete.
    if (previousItem) {
      const notInGroup = !Object.values(ELEMENTS_TYPES_GROUPS).includes(type);
      if (notInGroup) {
        const children = context.getters.getItemsChildren(id);
        const hasChildren = Array.isArray(children) && children.length > 0;
        const allowDrop = checks.checkAllowDrop({
          item: newObject,
          anotherItem: previousItem,
        });

        if (!hasChildren
          && !isSecondAction
          && allowDrop
        ) {
          const {
            id: previousId,
            type,
            returnToParent,
            relatedToItemId,
          } = previousItem;
          await context.dispatch('addNode', {
            id: previousId,
            from: id,
            type,
            returnToParent,
            relatedToItemId,
            isNew: false,
            isSecondAction: true,
          });
        } else {
          // await context.dispatch('deleteNodes', {
          //   id: previousItem.from,
          //   childrenOnly: true,
          //   excludeId: id,
          // });
          await context.dispatch('detachNode', { id: previousItem.id });
        }
      }
    }

    if (relatedToItemId) {
      newObject.relatedToItemId = relatedToItemId;
    }

    let items = [...context.state.items];

    if (isNew) {
      items.push(newObject);
    } else {
      for (const [index, i] of items.entries()) {
        if (i.id === id) {
          const temp = [...items];
          // Update
          temp[index] = newObject;
          // Move updated item to the end
          temp.push(temp.splice(index, 1)[0]);
          items = temp;
        }
      }
    }

    context.commit('updateField', {
      path: 'items',
      value: items,
    });
    context.commit('updateField', {
      path: 'draggingItem',
      value: state.draggingItem,
    });

    context.commit('resetTempVariables');

    if (addDefaultChild && Object.keys(ELEMENTS_TYPES_GROUPS).includes(type)) {
      await context.dispatch('addNode', {
        from: id,
        isNew: true,
        type: ELEMENTS_TYPES_GROUPS[type],
      });
    }

    return newObject;
  },

  /**
   * Swap nodes
   * @param context
   * @param id
   */
  swapNodes(context, { id }) {
    let items = [...context.state.items];
    const draggingItemId = objectValue(context, 'state.draggingItem.id');

    const draggingIndex = items.findIndex(i => i.id === draggingItemId);
    const droppingIndex = items.findIndex(i => i.id === id);

    if (typeof draggingIndex === 'undefined'
      || typeof droppingIndex === 'undefined'
    ) {
      return false;
    }

    items = swapArrayItems(items, draggingIndex, droppingIndex);

    context.commit('updateField', {
      path: 'items',
      value: items,
    });
    context.commit('updateField', {
      path: 'draggingItem',
      value: state.draggingItem,
    });

    context.commit('resetTempVariables');
  },

  /**
   * Detach node
   * @param context
   * @param id
   * @returns {Promise<void>}
   */
  async detachNode(context, { id }) {
    let ids = [id];

    // Detach each children
    // ids = [...ids, ...getChildrenIds({
    //   context,
    //   id,
    //   excludeItemsInGroup: true,
    // })];

    const items = [...context.state.items].map(i => {
      if (ids.includes(i.id)) {
        i.from = null;
        i.deleted = true;
      }

      return i;
    });

    context.commit('updateField', {
      path: 'items',
      value: items,
    });
  },

  /**
   * Delete nodes
   * @param context
   * @param id
   * @param childrenOnly
   * @param excludeId
   */
  async deleteNodes(context, {
    id,
    childrenOnly = false,
    excludeId = null,
  }) {
    const items = [...context.state.items];

    let toDelete = [...getChildrenIds({
      context,
      id,
      excludeId,
    })];

    // Remove all.
    for (const deleteId of toDelete) {
      const index = items.findIndex(i => i.id === deleteId);
      if (index > -1) {
        items.splice(index, 1);
      }
    }
    context.commit('updateField', {
      path: 'items',
      value: items,
    });
  },

  /**
   * Update node
   * @param context
   * @param node
   */
  async updateNode(context, {node}) {
    let items = [...context.state.items];

    items = items.map(i => {
      if (i.id === node.id) {
        return {
          ...i,
          ...node,
        };
      } else {
        return i;
      }
    });

    context.commit('updateField', {
      path: 'items',
      value: items,
    });
  },

  /**
   * Clean nodes
   * @param context
   * @returns {Promise<boolean>}
   */
  async cleanNodes(context) {
    const { items } = context.state;

    for (const i of items) {
      if (!i.isRoot) {
        const parent = items.find(_i => _i.id === i.from);
        if (!parent) {
          console.log('cleanNodes - !parent', { id: i.id });
          await context.dispatch('deleteNodes', { id: i.id });
        }
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
